/** @format */

import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useState } from "react";
import { Collegamento } from "./Collegamento";
import { API_ENDPOINTS } from "../../../../constants/api";
import { THome, THomeDocumento } from "../../../../redux-modules/declarations/maps/home";
import apiClient from "../../../../utils/apiClient";
import { usePermissionForRoute } from "../../../../utils/permissions/routeToPermissionMap";
import { Routes } from "../../../../utils/Routes";
import { showNotification } from "../../../../utils/showNotification";
import { messaggioErrore } from "../../ViewPresenze/usePresenze";
import { giornoMese, momentoPassato, scadenzaDocumento } from "../format";

interface Props {
  documenti: THome["documenti"];
  comunicazioni: THome["comunicazioni"];
  oggi: string;
  utenteId: string;
  /** dopo uno scaricamento: il documento risulta letto */
  onLetto: () => void;
}

/** Stessa sequenza della Home precedente: segna letto, poi scarica il file con l'autorizzazione. */
const scarica = (documento: THomeDocumento, utenteId: string, onLetto: () => void): void => {
  apiClient
    .post(
      API_ENDPOINTS.PERSONAL_DOCUMENTS_READ.replace(":id_document", String(documento.id)).replace(
        ":id_user",
        utenteId,
      ),
    )
    .then(onLetto)
    .catch(() => undefined);
  apiClient
    .get(API_ENDPOINTS.AUTH_DOCUMENT.replace(":document_id", String(documento.id)), {
      responseType: "blob",
    })
    .then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", documento.nome);
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch(err =>
      showNotification("error", "Oops..", messaggioErrore(err, "Documento non scaricato")),
    );
};

const DOCUMENTI_MOSTRATI = 4;

/** Documenti e comunicazioni recenti in due schede, con i collegamenti alle pagine complete. */
export const DocumentiComunicazioni = ({
  documenti,
  comunicazioni,
  oggi,
  utenteId,
  onLetto,
}: Props): JSX.Element => {
  const [scheda, setScheda] = useState<"documenti" | "comunicazioni">(
    comunicazioni.non_lette > 0 && documenti.non_letti === 0 ? "comunicazioni" : "documenti",
  );
  const mieiDocumenti = Routes.usersEditDocuments.replace(":id_user", utenteId);
  const vedeMieiDocumenti = usePermissionForRoute(mieiDocumenti as Routes);
  const vedeComunicazioni = usePermissionForRoute(Routes.communicationsView);
  const mieComunicazioni = Routes.usersEditCommunications.replace(":id_user", utenteId);
  const vedeMieComunicazioni = usePermissionForRoute(mieComunicazioni as Routes);

  const linkComunicazione = (id: number): string | null =>
    vedeComunicazioni
      ? Routes.communicationsView.replace(":id_communication", String(id))
      : vedeMieComunicazioni
      ? mieComunicazioni
      : null;
  const tutteLeComunicazioni = vedeComunicazioni
    ? Routes.comunications
    : vedeMieComunicazioni
    ? mieComunicazioni
    : null;

  return (
    <section className="scheda" aria-label="Documenti e comunicazioni">
      <div className="schede-mini" role="group" aria-label="Mostra">
        <button
          type="button"
          aria-pressed={scheda === "documenti"}
          onClick={(): void => setScheda("documenti")}
        >
          Documenti
          {documenti.non_letti > 0 && <span className="c">{documenti.non_letti}</span>}
        </button>
        <button
          type="button"
          aria-pressed={scheda === "comunicazioni"}
          onClick={(): void => setScheda("comunicazioni")}
        >
          Comunicazioni
          {comunicazioni.non_lette > 0 && <span className="c">{comunicazioni.non_lette}</span>}
        </button>
      </div>

      {scheda === "documenti" && (
        <>
          {documenti.elenco.length ? (
            <ul className="elenco">
              {documenti.elenco.slice(0, DOCUMENTI_MOSTRATI).map(d => {
                const scadenza = scadenzaDocumento(d.scadenza, oggi);
                return (
                  <li key={d.id}>
                    <span className="testo">
                      <span className={`titolo${d.letto_il ? " letto" : ""}`} title={d.nome}>
                        {d.nome}
                      </span>
                      <span className="sotto">
                        {[
                          d.tipo,
                          d.caricato_il
                            ? `caricato il ${giornoMese(d.caricato_il.substr(0, 10))}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    {!d.letto_il && <span className="pillola blu">Nuovo</span>}
                    {scadenza && (
                      <span className={`pillola ${scadenza.classe}`}>{scadenza.testo}</span>
                    )}
                    <Button
                      icon={<DownloadOutlined />}
                      aria-label={`Scarica ${d.nome}`}
                      title="Scarica"
                      onClick={(): void => scarica(d, utenteId, onLetto)}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className="vuoto">Nessun documento recente.</span>
          )}
          {vedeMieiDocumenti && (
            <Collegamento className="collegamento" to={mieiDocumenti}>
              I miei documenti
            </Collegamento>
          )}
        </>
      )}

      {scheda === "comunicazioni" && (
        <>
          {comunicazioni.elenco.length ? (
            <ul className="elenco">
              {comunicazioni.elenco.map(c => {
                const link = linkComunicazione(c.id);
                const classe = `titolo${c.letta_il ? " letto" : ""}`;
                return (
                  <li key={c.id}>
                    {c.letta_il ? (
                      <span className="letto-segno" aria-hidden="true" />
                    ) : (
                      <span className="non-letto" role="img" aria-label="Da leggere" />
                    )}
                    <span className="testo">
                      {link ? (
                        <Collegamento className={classe} to={link} title={c.titolo}>
                          {c.titolo}
                        </Collegamento>
                      ) : (
                        <span className={classe} title={c.titolo}>
                          {c.titolo}
                        </span>
                      )}
                      {c.inviata_il && (
                        <span className="sotto">{momentoPassato(c.inviata_il, oggi)}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className="vuoto">Nessuna comunicazione.</span>
          )}
          {tutteLeComunicazioni && (
            <Collegamento className="collegamento" to={tutteLeComunicazioni}>
              Tutte le comunicazioni
            </Collegamento>
          )}
        </>
      )}
    </section>
  );
};
